/**
 * @typedef {object} ExportSelection
 * @property {'all'|'selected'} scope - Alles exporteren of alleen de aangevinkte
 * @property {string[]} selectedIds - Aangevinkte ID's; alleen gebruikt bij 'selected'
 * @property {Date|null} startDate - Begin van de periode; null is geen ondergrens
 * @property {Date|null} endDate - Einde van de periode; null is geen bovengrens
 * @property {string[]} [groupIds] - Gekozen groepen (bijv. statussen); ontbreekt = geen groepsfilter
 */

/**
 * Filtert de rijen van een export op selectie en periode.
 *
 * De einddatum telt de hele dag mee: kiest iemand 26 augustus als einddatum,
 * dan hoort een registratie van die ochtend er nog bij.
 *
 * @param {Array<any>} rows - De rijen om te filteren
 * @param {ExportSelection} selection
 * @param {object} accessors
 * @param {(row: any) => string} accessors.getId - Waarop de selectie matcht
 * @param {(row: any) => Date|null} accessors.getDate - Datum van de rij; null telt altijd mee
 * @param {(row: any) => string} [accessors.getGroup] - Groep van de rij; alleen nodig bij `groupIds`
 * @returns {Array<any>} De rijen die aan beide voorwaarden voldoen
 */
const filterExportRows = (rows, selection, { getId, getDate, getGroup }) => {
    const { scope = 'all', selectedIds = [], startDate = null, endDate = null, groupIds } = selection || {}

    const allowed = new Set(selectedIds || [])
    // Geen `groupIds` is iets anders dan een lege lijst: het eerste is 'dit
    // venster kent geen groepen', het tweede is 'niets aangevinkt'.
    const groups = Array.isArray(groupIds) && getGroup ? new Set(groupIds) : null

    const from = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null
    const until = endDate ? new Date(endDate).setHours(23, 59, 59, 999) : null

    return (rows || []).filter((row) => {
        if (scope === 'selected' && !allowed.has(getId(row))) return false
        if (groups && !groups.has(getGroup(row))) return false

        const date = getDate ? getDate(row) : null
        // Een rij zonder datum kan niet buiten de periode vallen; die telt mee.
        if (!date) return true

        const time = date instanceof Date ? date.getTime() : new Date(date).getTime()
        if (Number.isNaN(time)) return true

        if (from !== null && time < from) return false
        if (until !== null && time > until) return false

        return true
    })
}

export default filterExportRows
